'use client';

import { Button } from '@heroui/button';
import { Form } from '@heroui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { OtpInput } from '@/components/auth/otp-input';
import { ResendControl } from '@/components/auth/resend-control';
import type { Session } from '@/lib/auth';
import { authClient } from '@/lib/auth-client';
import { useLoadingCallback } from '@/lib/hooks/use-loading-callback';
import { authToasts } from '@/lib/utils/toast';
import { otpSchema } from '@/schemas/auth';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../ui/dialog';

export const VerifyEmailDialog = ({
    children,
    isOpen,
    onOpenChange,
    session,
    isClosable = true,
}: {
    children?: ReactNode;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    session: Session | { user: { email: string } };
    isClosable?: boolean;
}) => {
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors, isValid },
    } = useForm<z.infer<typeof otpSchema>>({
        resolver: zodResolver(otpSchema),
        mode: 'onTouched',
    });
    const router = useRouter();
    const handleResendVerification = useCallback(async () => {
        if (!session?.user.email) {
            return;
        }

        const response = await authClient.emailOtp.sendVerificationOtp({
            email: session.user.email,
            type: 'email-verification',
        });

        if (response.error) {
            authToasts.emailVerificationFailed(
                response.error.message || 'Failed to resend verification email.'
            );
        } else {
            // Clear the OTP field when resending
            setValue('code', '');
            authToasts.emailVerificationSent();
        }

        return response;
    }, [session?.user.email, setValue]);

    const [handleVerification, isVerifying] = useLoadingCallback(
        async (data: z.infer<typeof otpSchema>) => {
            if (!session?.user.email) {
                return;
            }
            const response = await authClient.emailOtp.verifyEmail({
                otp: data.code,
                email: session.user.email,
            });

            if (response.error) {
                authToasts.emailVerificationFailed(
                    response.error.message ||
                        'Verification failed. Please try again.'
                );
            } else {
                authToasts.emailVerificationSuccess();
                router.refresh();
                onOpenChange(false);
            }
        }
    );

    return (
        <Dialog
            onOpenChange={isClosable ? onOpenChange : undefined}
            open={isOpen}
        >
            <DialogTrigger asChild>
                {children || <Button>Verify Email</Button>}
            </DialogTrigger>
            <DialogContent
                onEscapeKeyDown={
                    isClosable ? undefined : (e) => e.preventDefault()
                }
                onPointerDownOutside={
                    isClosable ? undefined : (e) => e.preventDefault()
                }
            >
                <DialogHeader>
                    <DialogTitle>Verify Your Email Address</DialogTitle>
                    <DialogDescription>
                        Please check your email for a verification code. If you
                        haven't received it, you can resend the verification
                        email.
                    </DialogDescription>
                </DialogHeader>
                <Form
                    className="mt-4 flex flex-col gap-4"
                    onSubmit={handleSubmit(handleVerification)}
                >
                    <Controller
                        control={control}
                        name="code"
                        render={({ field }) => (
                            <OtpInput
                                {...field}
                                errorMessage={errors.code?.message}
                                isInvalid={!!errors.code}
                                length={6}
                            />
                        )}
                    />
                    <div className="grid w-full grid-cols-2 gap-2">
                        <Button
                            color="primary"
                            fullWidth
                            isDisabled={!isValid}
                            isLoading={isVerifying}
                            type="submit"
                            variant="shadow"
                        >
                            Verify Email
                        </Button>
                        <ResendControl
                            onResend={handleResendVerification}
                            resendInterval={60}
                            variant="button"
                        >
                            Resend
                        </ResendControl>
                    </div>
                </Form>
            </DialogContent>
        </Dialog>
    );
};
